/* eslint-disable no-lonely-if */
import { useController } from "react-hook-form";
import { Box, Chip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import ErrorBox from "../shared/ErrorBox"; // <-- same as TextFieldLocal

const MultiChipField = ({ field, control, errors }) => {
  const {
    field: { value = field.multiSelect ? [] : "", onChange },
  } = useController({
    name: field.name,
    control,
    defaultValue: field.multiSelect ? [] : "",
    rules: {
      validate: (val) => {
        if (field.isRequired) {
          if (field.multiSelect && (!val || val.length === 0)) {
            return `${field.label || "This field"} is required`;
          }
          if (!field.multiSelect && !val) {
            return `${field.label || "This field"} is required`;
          }
        }
        return true;
      },
    },
  });

  const toggleValue = (optionValue) => {
    if (field.multiSelect) {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    } else {
      onChange(value === optionValue ? "" : optionValue);
    }
  };

  const isSelected = (optionValue) => (field.multiSelect ? value.includes(optionValue) : value === optionValue);

  const hasError = Boolean(errors[field.name]);

  const renderIcon = (icon) => {
    if (!icon) return null;

    // Case 1: MUI / React component
    if (typeof icon === "function") {
      const IconComponent = icon; // Use capitalized variable
      return <IconComponent fontSize="small" />;
    }

    // Case 2: Image / base64 / URL
    if (typeof icon === "string") {
      return (
        <div style={{ width: 16, height: 16 }}>
          <img src={icon} alt="icon" style={{ width: "100%", height: "100%" }} />
        </div>
      );
    }

    return null;
  };

  return (
    <Box>
      {field.label && (
        <Typography variant="h6" sx={{ paddingBottom: "12px" }}>
          {field.label}
          {field.isRequired && <span> *</span>}
        </Typography>
      )}

      {/* CHIP CONTAINER WITH ERROR BORDER */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={1}
        sx={{
          padding: "8px",
          borderRadius: "6px",
          border: hasError ? "1px solid rgba(222, 136, 133, 1)" : "",

          "&:hover": {
            border: hasError ? "1px solid rgba(222, 136, 133, 1)" : "",
          },
        }}>
        {field.options.map((opt) => (
          <Chip
            key={opt.value}
            icon={renderIcon(opt.icon)}
            label={opt.label}
            onClick={() => toggleValue(opt.value)}
            variant={isSelected(opt.value) ? "filled" : "outlined"}
            color={isSelected(opt.value) ? "primary" : "default"}
            sx={{ cursor: "pointer" }}
          />
        ))}
      </Box>

      {/* SAME ErrorBox AS TextFieldLocal */}
      <ErrorBox message={errors[field.name]?.message} showIcon={false} />
    </Box>
  );
};

MultiChipField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    isRequired: PropTypes.bool,
    multiSelect: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
        icon: PropTypes.element,
      }),
    ).isRequired,
  }).isRequired,
  control: PropTypes.shape({
    register: PropTypes.func,
    unregister: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
  }).isRequired,

  errors: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number, PropTypes.shape({})])).isRequired,
};

export default MultiChipField;
